import type { FlagRingServe } from 'definitions';
import type { RingDraft } from '@/state/flags/flag-drafts';
import type { FlagManager } from '@/state/flags/useFlag';
import type { AttributeRulesCopy } from '../../Experiments/Details/ExperimentSettings/ServerSideConfig/AttributeRulesEditor';
import { Plus, X } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRingsEditor } from '@/state/flags/useRingsEditor';
import { AttributeRulesEditor } from '../../Experiments/Details/ExperimentSettings/ServerSideConfig/AttributeRulesEditor';
import { useRuleDraft } from '../../Experiments/Details/ExperimentSettings/ServerSideConfig/use-rule-draft';
import { FlagSection, FIELD_CLASS, SaveStrip, SECONDARY_BUTTON_CLASS } from './FlagSection';
import { FLAG_COPY } from '../copy';

type FlagRingsProps = { manager: FlagManager };

const COPY = FLAG_COPY.rings;

/**
 * Groups decided before the percentage: staff On, a beta list On, key accounts Off. Each ring
 * matches on attribute rules (the Server-side experiment's rule editor), a list of IDs, or both.
 */
export const FlagRings = ({ manager }: FlagRingsProps) => {
  const editor = useRingsEditor(manager, COPY.saved);
  const isEditor = manager.isEditor;

  return (
    <FlagSection
      title={COPY.title}
      help={COPY.help}
      description={COPY.description}
      tourAnchor="flag-rings"
      footer={
        editor.isDirty ? (
          <SaveStrip
            blockingReason={editor.blockingReason}
            saveLabel={COPY.save}
            discardLabel={COPY.discard}
            onSave={editor.save}
            onDiscard={editor.discard}
            isSaving={editor.isSaving}
          />
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        {editor.rings.length === 0 && <p className="supporting m-0">{COPY.empty}</p>}
        {editor.rings.map((ring, index) => (
          <RingCard
            key={index}
            ring={ring}
            index={index}
            isEditor={isEditor}
            locked={editor.isLocked}
            onChange={(patch) => editor.updateRing(index, patch)}
            onRemove={() => editor.removeRing(index)}
          />
        ))}
        {editor.canAdd && (
          <div>
            <Button type="button" variant="outline" onClick={editor.addRing} className={SECONDARY_BUTTON_CLASS}>
              <Plus className="size-3.5" strokeWidth={1.5} />
              {COPY.add}
            </Button>
          </div>
        )}
      </div>
    </FlagSection>
  );
};

type RingCardProps = {
  ring: RingDraft;
  index: number;
  isEditor: boolean;
  locked: boolean;
  onChange: (patch: Partial<RingDraft>) => void;
  onRemove: () => void;
};

const RingCard = ({ ring, index, isEditor, locked, onChange, onRemove }: RingCardProps) => {
  // The rule dialog adds to the ring's draft; the rings are saved together.
  const ruleEditor = useRuleDraft({
    rules: ring.rules,
    onAdd: (rule) => {
      onChange({ rules: [...ring.rules, rule] });
      return true;
    },
  });
  const rulesCopy: AttributeRulesCopy = {
    label: COPY.rulesLabel,
    help: isEditor ? COPY.rulesHelpEditor : COPY.rulesHelpCode,
    empty: COPY.rulesEmpty,
    full: COPY.rulesFull,
    dialogHelp: [isEditor ? COPY.rulesHelpEditor : COPY.rulesHelpCode],
  };
  const idsId = `flag-ring-ids-${index}`;

  return (
    <div className="border-line rounded-card flex flex-col gap-3 border p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="micro-label text-foreground">
            {COPY.ring} {index + 1}
          </span>
          <span className="micro-label">{COPY.serve}</span>
          <Select
            value={ring.serve}
            onValueChange={(value) => onChange({ serve: value as FlagRingServe })}
            disabled={locked}
          >
            <SelectTrigger
              className={cn(FIELD_CLASS, 'h-8 w-20')}
              aria-label={`${COPY.ring} ${index + 1}: ${COPY.serve}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="on">On</SelectItem>
              <SelectItem value="off">Off</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {!locked && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`${COPY.remove} ${index + 1}`}
            title={COPY.remove}
            className="text-dim hover:text-danger hover:bg-hover focus-visible:ring-ring flex size-8 items-center justify-center rounded-lg focus-visible:ring-2 focus-visible:outline-none"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
        )}
      </div>

      <AttributeRulesEditor
        manager={{
          ...ruleEditor,
          rules: ring.rules,
          isSaving: locked,
          removeRule: (ruleIndex) => onChange({ rules: ring.rules.filter((_, idx) => idx !== ruleIndex) }),
        }}
        copy={rulesCopy}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor={idsId} className="micro-label">
          {COPY.ids}
        </label>
        <Textarea
          id={idsId}
          value={ring.idsText}
          onChange={(ev) => onChange({ idsText: ev.target.value })}
          disabled={locked}
          placeholder={isEditor ? COPY.idsHelpEditor : COPY.idsHelpCode}
          className="border-input bg-field text-foreground min-h-[64px] resize-y rounded-lg font-mono text-micro"
        />
      </div>
    </div>
  );
};
